import React, { useState } from 'react';
import { Ban, AlertTriangle, EyeOff } from 'lucide-react';

/**
 * RiskSettingsSelector - Component for configuring risk sensitivity settings
 * 
 * Used in step 4 of the onboarding wizard to set:
 * - Risk category sensitivity levels
 * - Custom keywords (optional)
 */
export function RiskSettingsSelector({ value, onChange, simplified = false }) {
	const [settings, setSettings] = useState(value || getDefaultSettings());
	const [customKeywords, setCustomKeywords] = useState('');
	const [showAdvanced, setShowAdvanced] = useState(false);

	// Update parent when settings change (but avoid infinite loops)
	React.useEffect(() => {
		if (settings && onChange) {
			onChange(settings);
		}
		// Value-only: onChange is a fresh function per parent render; depending
		// on it re-emits settings on every parent render and loops.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settings]);

	const handleSensitivityChange = (category, level) => {
		setSettings(prev => ({
			...prev,
			[category]: level,
		}));
	};

	const handleAddKeyword = () => {
		if (customKeywords.trim()) {
			const newKeywords = [...(settings.keywords || []), customKeywords.trim()];
			setSettings(prev => ({
				...prev,
				keywords: newKeywords,
			}));
			setCustomKeywords('');
		}
	};

	const handleRemoveKeyword = (keywordToRemove) => {
		const newKeywords = settings.keywords.filter(k => k !== keywordToRemove);
		setSettings(prev => ({
			...prev,
			keywords: newKeywords,
		}));
	};

	const sensitivityLevels = [
		{ value: 'low', label: 'Low', description: 'Only flag obvious violations' },
		{ value: 'medium', label: 'Medium', description: 'Standard monitoring level' },
		{ value: 'high', label: 'High', description: 'Flag potential issues' },
	];

	const riskCategories = [
		{
			id: 'hateSpeech',
			label: 'Hate Speech',
			description: 'Discriminatory or offensive language',
			icon: Ban,
		},
		{
			id: 'harassment',
			label: 'Harassment',
			description: 'Bullying or threatening behavior',
			icon: AlertTriangle,
		},
		{
			id: 'explicitContent',
			label: 'Explicit Content',
			description: 'Inappropriate or adult content',
			icon: EyeOff,
		},
	];

	if (simplified) {
		// Simplified version for onboarding
		return (
			<div className="space-y-6">
				<div>
					<h4 className="text-sm font-medium text-foreground mb-3">
						Choose Monitoring Sensitivity
					</h4>
					<p className="text-sm text-muted-foreground mb-4">
						Select how sensitive RepCheck should be when monitoring content.
					</p>

					<div className="grid gap-4">
						{sensitivityLevels.map((level) => (
							<button
								key={level.value}
								type="button"
								onClick={() => {
									// Set all categories to the same level
									const newSettings = {};
									riskCategories.forEach(category => {
										newSettings[category.id] = level.value;
									});
									setSettings(prev => ({ ...prev, ...newSettings }));
								}}
								className={`
									p-4 border rounded-lg text-left transition-all duration-200
									${settings.hateSpeech === level.value &&
										settings.harassment === level.value &&
										settings.explicitContent === level.value
										? 'border-brand-500 bg-brand-600 text-white'
										: 'border-input hover:border-input bg-card'
									}
								`}
							>
								<div className="flex items-start">
									<div className="flex-1">
										<div className="font-medium">{level.label}</div>
										<div className="text-sm opacity-75">{level.description}</div>
									</div>
									{settings.hateSpeech === level.value &&
										settings.harassment === level.value &&
										settings.explicitContent === level.value && (
											<svg
												className="w-5 h-5 ml-2"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M5 13l4 4L19 7"
												/>
											</svg>
										)}
								</div>
							</button>
						))}
					</div>
				</div>

				{/* Custom keywords section */}
				<div>
					<h4 className="text-sm font-medium text-foreground mb-3">
						Custom Keywords (Optional)
					</h4>
					<p className="text-sm text-muted-foreground mb-3">
						Add specific words or phrases you want to monitor.
					</p>

					<div className="flex gap-2 mb-3">
						<input
							type="text"
							value={customKeywords}
							onChange={(e) => setCustomKeywords(e.target.value)}
							placeholder="Enter keyword..."
							className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
							onKeyPress={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									handleAddKeyword();
								}
							}}
						/>
						<button
							type="button"
							onClick={handleAddKeyword}
							disabled={!customKeywords.trim()}
							className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Add
						</button>
					</div>

					{settings.keywords && settings.keywords.length > 0 && (
						<div className="flex flex-wrap gap-2">
							{settings.keywords.map((keyword, index) => (
								<div
									key={index}
									className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-sm"
								>
									<span>{keyword}</span>
									<button
										type="button"
										onClick={() => handleRemoveKeyword(keyword)}
										className="text-muted-foreground hover:text-foreground"
									>
										×
									</button>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		);
	}

	// Full version for advanced settings
	return (
		<div className="space-y-6">
			<div>
				<h4 className="text-sm font-medium text-foreground mb-3">
					Risk Category Settings
				</h4>

				<div className="space-y-4">
					{riskCategories.map((category) => (
						<div key={category.id} className="border rounded-lg p-4">
							<div className="flex items-center mb-3">
								<category.icon className="mr-2 h-5 w-5 text-muted-foreground" aria-hidden />
								<div>
									<div className="font-medium">{category.label}</div>
									<div className="text-sm text-muted-foreground">{category.description}</div>
								</div>
							</div>

							<div className="grid grid-cols-3 gap-2">
								{sensitivityLevels.map((level) => (
									<button
										key={level.value}
										type="button"
										onClick={() => handleSensitivityChange(category.id, level.value)}
										className={`
											p-2 text-sm border rounded transition-all duration-200
											${settings[category.id] === level.value
												? 'border-brand-500 bg-brand-600 text-white'
												: 'border-input hover:border-input bg-card'
											}
										`}
									>
										{level.label}
									</button>
								))}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Advanced settings toggle */}
			<div>
				<button
					type="button"
					onClick={() => setShowAdvanced(!showAdvanced)}
					className="text-sm text-muted-foreground hover:text-foreground flex items-center"
				>
					{showAdvanced ? 'Hide' : 'Show'} advanced settings
					<svg
						className={`w-4 h-4 ml-1 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M19 9l-7 7-7-7"
						/>
					</svg>
				</button>

				{showAdvanced && (
					<div className="mt-4 p-4 bg-muted rounded-lg">
						<h5 className="text-sm font-medium text-foreground mb-3">Custom Keywords</h5>

						<div className="flex gap-2 mb-3">
							<input
								type="text"
								value={customKeywords}
								onChange={(e) => setCustomKeywords(e.target.value)}
								placeholder="Enter keyword..."
								className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
								onKeyPress={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										handleAddKeyword();
									}
								}}
							/>
							<button
								type="button"
								onClick={handleAddKeyword}
								disabled={!customKeywords.trim()}
								className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Add
							</button>
						</div>

						{settings.keywords && settings.keywords.length > 0 && (
							<div className="flex flex-wrap gap-2">
								{settings.keywords.map((keyword, index) => (
									<div
										key={index}
										className="flex items-center gap-1 px-2 py-1 bg-card border rounded-md text-sm"
									>
										<span>{keyword}</span>
										<button
											type="button"
											onClick={() => handleRemoveKeyword(keyword)}
											className="text-muted-foreground hover:text-foreground"
										>
											×
										</button>
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function getDefaultSettings() {
	return {
		hateSpeech: 'medium',
		harassment: 'medium',
		explicitContent: 'medium',
		keywords: [],
	};
} 